import React, { useState, useEffect, useRef, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLocation, Link } from 'react-router-dom';
import moment from 'moment';
import { isEmpty, uniqBy, flatMap } from 'lodash';
import { Container, Row, Col, OverlayTrigger, Popover, ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import RenderingManualItems from './RenderingManualItems';
import Loader from '../../Components/Utility/Loader/Loader';
import * as action from './store/ProductCP.action';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';
import { shortenandHoverString, createOptions, transportArray } from '../../Components/helper';
import { toasterMsg } from '../../Components/Toaster/Toaster';
import { useCPDetailState } from './contextProvider/cpDetailProvider';
import ColumnFilter from './ColumnFilter';

function DetailTablePO(props) {
  const {
    handleNextPageInScroll,
    expandAllProductInfo,
    onSelectCheckBox,
    expandProductInfo,
    handleSelectAllColumnOfTabs,
    bulkUpdate,
    copyValue,
    setCopyValue,
    changeManualInputs,
    pcpId,
    showRequiredOnly,
    selectedPinnedColumns,
  } = props;
  const { t } = useTranslation('common');
  const plannedWithcolon = `${t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.PLANNED_COL')} `;
  const {
    Users: { userGroupsCardList, logInRights },
    ProductCPReducer: { supplierList, productCpColumnTabs, individualDetail },
    UserDetails: { isSupplierLoggedIn, userInfo, isCompanyUserLoggedIn },
  } = useSelector((reducer) => reducer);
  const dispatch = useDispatch();
  const [isOpenDependent, setIsOpenDependent] = useState({});
  const [dependentFilter, setDependentFilter] = useState({});
  const [activeColumnId, setActiveColumnId] = useState(null);
  const previousFilterRef = useRef({});
  const [isPinnedColum, setIsPinnedColumn] = useState(false);
  const [columnOptionLoader, setColumnOptionLoader] = useState(false);
  const [totalPinnedColum, setIsTotalPinnedColumn] = useState(null);
  const [activeZindexId, setActiveZindexId] = useState();
  const [renderPinnedColumns, setRenderPinnedColumns] = useState([]);
  const [filteredPinnedColumns, setFilteredPinnedColumns] = useState([]);
  const isExpandingRef = useRef(false);

  const handleTogglePopover = (columnId) => {
    setActiveColumnId(activeColumnId === columnId ? null : columnId);
  };

  const { CheckActionPermission } = useComponent();
  const pcpAddEditAccess = CheckActionPermission(logInRights, 'criticalPath', 'product', 'addEdit');
  const location = useLocation();
  const loggedInUserId = userInfo?.userId;
  const searchParams = new URLSearchParams(location?.search);
  const locaStates = location?.state;
  const fromArch = searchParams.get('fromArchive');
  const isProductArchived = locaStates?.fromArchive || Boolean(fromArch);
  const cellGreen = 'cell-green';
  const cellAmber = 'cell-amber';
  const ansDateForamte = 'DD-MMM-YYYY';
  const color = 'div-td-h100';
  const cellClassName = 'h-1px';
  const hasFilterPermission = ['Text_area', 'Textbox', 'Date_picker', 'Number_Integer'];
  const { detailState, setDetailState, columnsFilterData, setColumnsFilterData, getTabColumn } = useCPDetailState();
  const checkAssigne = (columnElement) => {
    if (detailState?.isAssigneChecked) {
      return columnElement.assignee?.includes(loggedInUserId);
    }
    return true;
  };

  const showColumn = (columnElement) => {
    const specialColumns = ['colorway', 'supplier', 'factory', 'skuNumber', 'transport', 'size', 'location', 'quantity', 'approvedByDate', 'exFactoryDate', 'shipmentDate'];
    const isSpecialColumn = specialColumns.includes(columnElement?._id);
    return (
      checkAssigne(columnElement) &&
      (isSupplierLoggedIn
        ? detailState?.selectedColumnShow?.includes(columnElement?._id) && (isSpecialColumn || columnElement?.isVisibleToSupplier)
        : detailState?.selectedColumnShow?.includes(columnElement?._id))
    );
  };

  useEffect(() => {
    if (!detailState?.dependentFilter || Object.keys(detailState?.dependentFilter)?.length === 0) {
      setDetailState((prev) => ({
        ...prev,
        dependentFilter,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentFilter]);

  useEffect(() => {
    const applyOnTimeLateFilter = async () => {
      if (!getTabColumn) return;

      const currentFilterString = JSON.stringify(dependentFilter);
      const previousFilterString = JSON.stringify(previousFilterRef.current);

      if (currentFilterString === previousFilterString) {
        return;
      }

      const hadPreviousFilters = Object.keys(previousFilterRef.current).length > 0;
      previousFilterRef.current = { ...dependentFilter };

      const activeFilters = Object.keys(dependentFilter).filter((columnId) => dependentFilter[columnId] === 'onTime' || dependentFilter[columnId] === 'late');

      if (activeFilters.length > 0) {
        const columnFilters = {};
        activeFilters.forEach((columnId) => {
          const filterValue = dependentFilter[columnId];
          columnFilters[columnId] = {
            onTimeLateFilter: filterValue,
          };
        });
        await getTabColumn({
          columnFilters,
        });
      } else if (hadPreviousFilters) {
        await getTabColumn({});
      }
    };

    applyOnTimeLateFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentFilter]);

  useEffect(() => {
    if (selectedPinnedColumns?.length > 0) {
      setIsPinnedColumn(true);
      setIsTotalPinnedColumn(selectedPinnedColumns?.length);
    } else {
      setIsPinnedColumn(false);
      setIsTotalPinnedColumn(0);
    }
  }, [selectedPinnedColumns]);

  const createSpecialColumn = (columnId, sequence) => {
    const columnMap = {
      colorway: { name: 'Colourway', _id: 'colorway' },
      supplier: { name: 'Supplier', _id: 'supplier' },
      factory: { name: 'Factory', _id: 'factory' },
    };
    return { ...columnMap[columnId], sequence };
  };

  const PO_STATIC_PINNABLE_IDS = ['skuNumber', 'transport', 'size', 'location', 'quantity', 'approvedByDate', 'exFactoryDate', 'shipmentDate'];

  const SPECIAL_COLUMNS_CONFIG = {
    poRef: {
      _id: 'poRef',
      name: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.PO_REF'),
      columnType: 'po-based',
      dbField: 'purchaseOrderNumber',
      dbLevel: 'po',
      rowField: 'poNumber',
      hasNotFilled: false,
    },
    productName: {
      _id: 'productName',
      name: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.PRODUCT_NUMBER'),
      columnType: 'po-based',
      dbField: 'orderDetails.productName',
      dbLevel: 'orderDetails',
      rowField: 'productName',
      hasNotFilled: false,
    },
    transport: {
      _id: 'transport',
      name: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.TRANSPORT'),
      columnType: 'po-based',
      rowField: 'transport',
      hasNotFilled: false,
    },
    size: {
      _id: 'size',
      name: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SIZE'),
      columnType: 'po-based',
      rowField: 'sizeName',
      hasNotFilled: false,
    },
    location: {
      _id: 'location',
      name: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.LOCATION'),
      columnType: 'po-based',
      dbField: 'orderDetails.location',
      dbLevel: 'orderDetails',
      rowField: 'location',
      hasNotFilled: false,
    },
    skuNumber: {
      _id: 'skuNumber',
      name: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SKU_NUMBER'),
      columnType: 'po-based',
      dbField: 'orderDetails.skuNumber',
      dbLevel: 'orderDetails',
      rowField: 'skuNumber',
      hasNotFilled: true,
    },
    quantity: {
      _id: 'quantity',
      name: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.POQUANTITY'),
      columnType: 'po-based',
      dbField: 'orderDetails.quantity',
      dbLevel: 'orderDetails',
      rowField: 'quantity',
      hasNotFilled: false,
    },
    approvedByDate: {
      _id: 'approvedByDate',
      name: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.POAPPROVEDBYDATE'),
      columnType: 'po-based',
      dbField: 'orderDetails.approvedByDate',
      dbLevel: 'orderDetails',
      rowField: 'approvedByDate',
      hasNotFilled: false,
    },
    exFactoryDate: {
      _id: 'exFactoryDate',
      name: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.EXFACTORYDATE'),
      columnType: 'po-based',
      dbField: 'orderDetails.exFactoryDate',
      dbLevel: 'orderDetails',
      rowField: 'exFactoryDate',
      hasNotFilled: false,
    },
    shipmentDate: {
      _id: 'shipmentDate',
      name: t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SHIPMENTDATE'),
      columnType: 'po-based',
      dbField: 'orderDetails.shipmentDate',
      dbLevel: 'orderDetails',
      rowField: 'shipmentDate',
      hasNotFilled: false,
    },
  };

  useEffect(() => {
    const pinnedColumns = [];
    const priorityColumns = ['colorway', 'supplier', 'factory'];
    const isPOBased = individualDetail?.basedOnPO === true;

    selectedPinnedColumns?.forEach((selectedId) => {
      if (priorityColumns.includes(selectedId)) {
        pinnedColumns.push(createSpecialColumn(selectedId, pinnedColumns.length + 1));
      } else if (isPOBased && PO_STATIC_PINNABLE_IDS.includes(selectedId) && detailState?.selectedColumnShow?.includes(selectedId)) {
        const config = SPECIAL_COLUMNS_CONFIG[selectedId];
        if (config) {
          pinnedColumns.push({
            _id: config._id,
            name: config.name,
            columnType: config.columnType,
            rowField: config.rowField,
          });
        }
      } else {
        const col = detailState?.columnsTab?.find((c) => c._id === selectedId);
        if (col && (!showRequiredOnly || col.isRequired) && showColumn(col)) {
          pinnedColumns.push(col);
        }
      }
    });

    setRenderPinnedColumns(pinnedColumns);
    setFilteredPinnedColumns(pinnedColumns?.map((col) => col._id));
  }, [selectedPinnedColumns, detailState?.columnsTab, detailState?.selectedColumnShow, individualDetail?.basedOnPO]);

  useEffect(() => {
    if (detailState?.savedScrollLeft !== undefined && detailState?.lastUpdate) {
      const scrollContainer = document.getElementById('CPTableContainer');
      if (scrollContainer) {
        requestAnimationFrame(() => {
          scrollContainer.scrollLeft = detailState.savedScrollLeft;
        });
      }
    }
  }, [detailState?.lastUpdate, detailState?.savedScrollLeft]);

  const pinedColumLength = () => {
    const pinLabels = ['Zero-pin', 'One-pin', 'Two-pin', 'Three-pin', 'Four-pin', 'Five-pin'];
    return pinLabels[totalPinnedColum] || 'Zero-pin';
  };

  const callDatePickerApi = (colId, ind, devId, expColInd) => {
    if (expColInd !== undefined && expColInd !== null) {
      setActiveZindexId(`${colId}_${ind}_${devId}_${expColInd}`);
    } else {
      setActiveZindexId(`${colId}_${ind}_${devId}`);
    }
  };

  const expandButtons = (devEle, devIndex) => {
    return devEle?.isExpanded ? (
      <button
        type="button"
        className="btn-up-down"
        aria-label={t('COMMON_LABEL.EXPAND')}
        onClick={() => {
          isExpandingRef.current = true;
          expandProductInfo(devIndex);
          setTimeout(() => {
            isExpandingRef.current = false;
          }, 500);
        }}
        disabled={detailState?.tableLoading}
      >
        <span className="icon-new-down-arrow-icon icon-inverse " />
      </button>
    ) : (
      <button
        type="button"
        className="btn-up-down"
        aria-label={t('COMMON_LABEL.COLLAPSE')}
        onClick={() => {
          isExpandingRef.current = true;
          expandProductInfo(devIndex);
          setTimeout(() => {
            isExpandingRef.current = false;
          }, 500);
        }}
        disabled={detailState?.tableLoading}
      >
        <span className="icon-new-down-arrow-icon" />
      </button>
    );
  };

  const colourCode = (colEle) => {
    const today = moment().startOf('day');
    let dateColour = 'text-gray';

    if (colEle.estimatedDate) {
      const estimatedMoment = moment(colEle.estimatedDate).startOf('day');
      const oneDayPriorEstimated = estimatedMoment.clone().subtract(1, 'days');

      if (estimatedMoment.isSame(today, 'day') || oneDayPriorEstimated.isSame(today, 'day')) {
        dateColour = 'text-yellow';
      } else if (colEle.columnValue) {
        const columnValueMoment = moment(colEle.columnValue).startOf('day');

        if (estimatedMoment.isSame(columnValueMoment, 'day')) {
          dateColour = 'text-green';
        } else if (estimatedMoment.isAfter(columnValueMoment, 'day')) {
          dateColour = 'text-green';
        } else {
          dateColour = 'text-red';
        }
      }
    }

    return dateColour;
  };

  const colourCodeForDurationDate = (data) => {
    const { durationDays, columnValue } = data;
    if (!isNaN(durationDays) && typeof durationDays === 'number') {
      if (durationDays < 0) {
        return 'back-green';
      }
      if (durationDays > 0) {
        return 'back-red';
      }
      return 'back-amber';
    }

    const momentDuration = moment(durationDays, 'DD-MMMM-YYYY', true);
    const momentColumn = moment(columnValue, moment.ISO_8601, true);

    if (momentDuration.isAfter(momentColumn)) {
      return 'back-green';
    }
    if (momentDuration.isBefore(momentColumn)) {
      return 'back-red';
    }

    return 'back-amber';
  };

  const disablingConditions = (item) => {
    const group = userGroupsCardList.find((e) => e?._id === item?.assignee);
    const groupUsers = group?.users;
    return (
      isProductArchived ||
      !pcpAddEditAccess ||
      (isSupplierLoggedIn && !item?.assignee.includes(loggedInUserId)) ||
      (pcpAddEditAccess && isCompanyUserLoggedIn && item?.assignee && group && !groupUsers?.some((user) => user?._id === loggedInUserId))
    );
  };

  const getDateDifference = (date1, date2) => {
    if (!isNaN(date1) && typeof date1 === 'number') {
      return Math.abs(date1);
    }

    const momentDate1 = moment(date1, 'DD-MMMM-YYYY', true);
    const momentDate2 = moment(date2, moment.ISO_8601, true);

    if (!momentDate1.isValid() || !momentDate2.isValid()) {
      return 'Invalid date';
    }
    return Math.abs(momentDate2.diff(momentDate1, 'days'));
  };

  const matchesPORow = (dataItem, devEle) => {
    return dataItem?.rowKey === devEle?.rowKey;
  };

  const getColourType = (colEle) => {
    if (colEle?.columnType === 'manual') {
      if (colEle?.manualType?.key === 'Date_picker') return 'date';
      if (colEle?.manualType?.key === 'Number_percentage') return 'percentage';
    } else if (colEle?.columnType === 'source') {
      if (colEle?.source?.output?.name === 'date_added') return 'date';
      if (colEle?.source?.output?.name === 'percentage_of_approval') return 'percentage';
    }
  };

  const getPercentageColourClass = (cEle, verifyAgainst) => {
    const percent = percentageCheck(cEle, parseFloat(verifyAgainst));
    if (!percent) {
      return 'cell-grey';
    }

    switch (percent) {
      case 'green':
        return cellGreen;
      case 'red':
        return 'cell-red';
      case 'amber':
        return cellAmber;
      default:
        return 'cell-grey';
    }
  };
  const checkZindex = (id) => {
    if (id === activeZindexId) {
      return 'zindex-10';
    }
  };

  const getVerifyAgainstValue = (colEle, devEle) => {
    const copyData = detailState?.tableColumData ?? [];
    let mainObject = null;

    copyData.forEach((mainDEle) => {
      if (mainDEle.columnId === colEle?._id && matchesPORow(mainDEle, devEle) && mainDEle.columnValue) {
        mainObject = mainDEle;
      }
    });

    return mainObject?.columnValue;
  };

  const getDateColourClass = (cEle, verifyAgainst, devEle, expandEle = null) => {
    const finalCheckDate = moment(verifyAgainst);
    const { colourValue } = cEle;
    const rowToUse = expandEle || devEle;
    const checkValue = expandEle ? getVerifyAgainstValueForExpand({ _id: colourValue }, expandEle) : getVerifyAgainstValue({ _id: colourValue }, devEle);
    if (checkValue) {
      const momentValue = moment(checkValue);
      switch (cEle.colourType) {
        case 'green':
          if (finalCheckDate && momentValue) {
            return finalCheckDate?.isBefore(momentValue, 'day') || finalCheckDate?.isSame(momentValue, 'day') ? cellGreen : '';
          }
          break;
        case 'amber': {
          if (cEle?.endPercentage) {
            const addedDate = momentValue?.clone()?.add(cEle?.endPercentage, 'days')?.endOf('day');
            return finalCheckDate?.isBetween(momentValue, addedDate, 'days') || finalCheckDate?.isSame(addedDate, 'days') ? cellAmber : '';
          }
          break;
        }
        case 'red': {
          if (finalCheckDate && momentValue) {
            return finalCheckDate?.isAfter(momentValue, 'day') || finalCheckDate?.isSame(momentValue, 'day') ? 'cell-red' : '';
          }
          break;
        }
        default:
          return '';
      }
    }
  };

  const onCalendarClose = async () => {
    setActiveZindexId(null);
  };
  const getColourClass = (colourCoding, verifyAgainst, colourType, devEle, expandEle = null) => {
    let sourceColour = 'cell-grey';
    if (colourCoding?.length > 0) {
      for (let i = 0; i < colourCoding.length; i++) {
        const cEle = colourCoding[i];
        switch (colourType) {
          case 'date': {
            if (!sourceColour || sourceColour === 'cell-grey') {
              sourceColour = getDateColourClass(cEle, verifyAgainst, devEle, expandEle);
            }
            break;
          }
          case 'percentage':
            if (!sourceColour || sourceColour === 'cell-grey') {
              sourceColour = getPercentageColourClass(cEle, verifyAgainst);
            }
            break;
          default:
            break;
        }
      }
    }
    return sourceColour;
  };

  const colourClassName = (colEle, devEle) => {
    if (!colEle?.isColourCodingActive) return;

    const verifyAgainst = getVerifyAgainstValue(colEle, devEle);
    if (!verifyAgainst) return 'deleted';

    const colourCoding = colEle?.colourCoding;
    const colourType = getColourType(colEle);
    return getColourClass(colourCoding, verifyAgainst, colourType, devEle);
  };

  const elseRenderValue = (dataArray, parentIndex, columnElement, devEle, i, devIndex) => {
    const findex = dataArray?.findIndex((e) => e?.columnId === columnElement?._id && matchesPORow(e, devEle));
    if (findex !== -1) {
      return (
        <>
          {dataArray?.[findex]?.estimatedDate && (
            <div className={`${colourCode(dataArray?.[findex])} f-12 text-bold`}>
              {plannedWithcolon}
              {moment.isMoment(dataArray?.[findex]?.estimatedDate)
                ? dataArray?.[findex]?.estimatedDate.format(ansDateForamte)
                : moment(dataArray?.[findex]?.estimatedDate).format(ansDateForamte)}
            </div>
          )}
          {dataArray?.[findex]?.durationDays && dataArray?.[findex]?.columnValue && (
            <div className="d-flex w-100">
              <span className={`${colourCodeForDurationDate(dataArray?.[findex])} linkedColour duration-counter`}>
                {getDateDifference(dataArray?.[findex]?.durationDays, dataArray?.[findex]?.columnValue)}
              </span>
            </div>
          )}

          <RenderingManualItems
            {...columnElement}
            keyName={`${columnElement?.manualType?.key}`}
            keyprop={`${columnElement?.manualType?.key}${columnElement?._id}_${i}_${devEle?.rowKey}`}
            value={dataArray?.[findex]?.columnValue || null}
            placeholder={t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.ENTER_VALUE')}
            disableCondition={disablingConditions(columnElement) || bulkUpdate}
            change={(e) => changeManualInputs(e, columnElement, devEle, null, false)}
            options={columnElement?.options}
            characterLimit={columnElement?.characterLimit}
            labelColours={columnElement?.labelColoursOptions}
            subType={columnElement?.manualType?.subType}
            status={columnElement?.status}
            onCalendarOpen={() => callDatePickerApi(columnElement?._id, i, devEle?.rowKey)}
            onCalendarClose={() => onCalendarClose()}
            onMenuOpen={() => callDatePickerApi(columnElement?._id, i, devEle?.rowKey)}
            onMenuClose={() => onCalendarClose()}
          />
        </>
      );
    }
    return (
      <RenderingManualItems
        {...columnElement}
        keyName={`${columnElement?.manualType?.key}`}
        keyprop={`${columnElement?.manualType?.key}${columnElement?._id}_${i}_${devEle?.rowKey}`}
        value={dataArray?.[parentIndex]?.columnValue || null}
        placeholder={t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.ENTER_VALUE')}
        disableCondition={disablingConditions(columnElement) || bulkUpdate}
        change={(e) => changeManualInputs(e, columnElement, devEle, null, false)}
        options={columnElement?.options}
        characterLimit={columnElement?.characterLimit}
        labelColours={columnElement?.labelColoursOptions}
        subType={columnElement?.manualType?.subType}
        status={columnElement?.status}
        onCalendarOpen={() => callDatePickerApi(columnElement?._id, i, devEle?.rowKey)}
        onCalendarClose={() => onCalendarClose()}
        onMenuOpen={() => callDatePickerApi(columnElement?._id, i, devEle?.rowKey)}
        onMenuClose={() => onCalendarClose()}
      />
    );
  };

  const renderValues = (columnElement, i, devEle, relatedData, parentIndex) => {
    return (
      <>
        {relatedData?.[0]?.estimatedDate && (
          <div className={`${colourCode(relatedData?.[0])} f-12 text-bold`}>
            {plannedWithcolon}
            {moment.isMoment(relatedData[0].estimatedDate)
              ? relatedData[0].estimatedDate.format(ansDateForamte)
              : moment(relatedData[0].estimatedDate).format(ansDateForamte)}
          </div>
        )}
        {relatedData?.[0]?.durationDays && relatedData?.[0]?.columnValue && (
          <div className="d-flex w-100">
            <span className={`${colourCodeForDurationDate(relatedData?.[0])} linkedColour duration-counter`}>
              {getDateDifference(relatedData[0].durationDays, relatedData[0].columnValue)}
            </span>
          </div>
        )}
        <RenderingManualItems
          {...columnElement}
          keyName={`${relatedData?.[0]?.type}`}
          keyprop={`${relatedData?.[0]?.type}${columnElement?._id}_${i}_${devEle?.rowKey}`}
          value={relatedData?.[0]?.columnValue || null}
          placeholder={t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.ENTER_VALUES')}
          disableCondition={disablingConditions(columnElement) || bulkUpdate}
          change={(e) => changeManualInputs(e, columnElement, devEle, null, false)}
          options={columnElement?.options}
          characterLimit={columnElement?.characterLimit}
          labelColours={columnElement?.labelColoursOptions}
          subType={columnElement?.manualType?.subType}
          status={columnElement?.status}
          onCalendarOpen={() => callDatePickerApi(columnElement?._id, i, devEle?.rowKey)}
          onCalendarClose={() => onCalendarClose()}
          onMenuOpen={() => callDatePickerApi(columnElement?._id, i, devEle?.rowKey)}
          onMenuClose={() => onCalendarClose()}
        />
      </>
    );
  };

  const hasNoValues = (values) => values.every((val) => val === null || val === undefined);

  const areValuesSimilar = (values) => {
    const flatenedArray = values.flatMap((num) => (Array.isArray(num) ? JSON.stringify(num) : num));
    return values?.[0]?.type === 'date'
      ? values.every((val) => moment(val).format() === moment(values[0]).format())
      : flatenedArray.every((val) => JSON.stringify(val) === JSON.stringify(flatenedArray[0]));
  };

  const renderMultiple = () => 'Multiple';

  const percentageCheck = (percentageElement, verifyAgainst) => {
    switch (percentageElement?.colourValue) {
      case 'greater_than':
        return parseFloat(percentageElement?.startPercentage) <= parseFloat(verifyAgainst) ? percentageElement?.colourType : null;
      case 'between':
        return (parseFloat(percentageElement?.startPercentage) <= parseFloat(verifyAgainst) &&
          parseFloat(percentageElement?.endPercentage) >= parseFloat(verifyAgainst)) ||
          (parseFloat(percentageElement?.endPercentage) <= parseFloat(verifyAgainst) &&
            parseFloat(percentageElement?.startPercentage) >= parseFloat(verifyAgainst))
          ? percentageElement?.colourType
          : null;
      case 'less_than':
        return parseFloat(percentageElement?.startPercentage) >= parseFloat(verifyAgainst) ? percentageElement?.colourType : null;
      case 'equal':
        return parseFloat(percentageElement?.startPercentage) === parseFloat(verifyAgainst) ? percentageElement?.colourType : null;
      default:
        return null;
    }
  };

  const valueChange = (arrEle, answer) => {
    if (arrEle?.type === 'date') {
      answer.push(moment(arrEle?.columnValue).format(ansDateForamte));
    } else if (Array.isArray(arrEle)) {
      answer?.push(arrEle?.columnValue).join(', ');
    } else if (typeof arrEle === 'object' && !Object.keys(arrEle).includes('colour')) {
      const str = Object.keys(arrEle).map((key) => `${key}:${arrEle[key]}`);
      answer.push(str);
    } else {
      answer.push(arrEle?.columnValue);
    }
  };

  /**
   * Resolve the planned + actual value for a source cell. The backend emits the
   * dependent planned date (estimatedDate) and the source's actual value as two
   * separate tableColumData rows sharing the same columnId + rowKey, so a single
   * last-match-wins pass would let the value row overwrite the planned row (or
   * vice versa). Render the planned date from whichever matching row carries it,
   * independently of the value rendering, so both show together.
   */
  const findResultValue = (columnEle, rowKey) => {
    const matching = (detailState?.tableColumData || []).filter(
      (colEle) => colEle.columnId === columnEle?._id && colEle.rowKey === rowKey,
    );
    if (matching.length === 0) return null;

    const plannedEntry = matching.find((colEle) => colEle.estimatedDate);
    const valueEntry = matching.find((colEle) => colEle.columnValue);
    // Planned and actual live on separate rows for source columns; merge them so
    // colourCode can compare (green when actual is on/before planned, red when late)
    // and match the manual column's `f-12 text-bold` styling.
    const plannedColourRef = { estimatedDate: plannedEntry?.estimatedDate, columnValue: valueEntry?.columnValue };
    const plannedNode = plannedEntry ? (
      <div className={`${colourCode(plannedColourRef)} f-12 text-bold`}>
        {plannedWithcolon}{moment(plannedEntry.estimatedDate).format(ansDateForamte)}
      </div>
    ) : null;

    let val = null;
    matching.forEach((colEle) => {
      if (!colEle.columnValue) return;
      if (Array.isArray(colEle.columnValue)) {
        const valueArray = [];
        colEle?.columnValue?.forEach((resultEle) => {
          if (resultEle) {
            if (typeof resultEle === 'object') {
              valueChange(resultEle, valueArray);
              val = shortenandHoverString(valueArray?.filter((e) => e !== '0%').join(', '), 20, 'auto');
            } else {
              val = isEmpty(colEle.columnValue)
                ? null
                : shortenandHoverString(colEle.columnValue.filter((e) => e !== '0%').join(', '), 20, 'auto');
            }
          }
        });
      } else {
        const objectValue =
          (columnEle?.source?.output?.name !== 'date_added' || columnEle?.source?.subLocation?.type !== 'date') && typeof colEle?.columnValue === 'object'
            ? shortenandHoverString(colEle?.columnValue?.name, 20, 'auto')
            : columnEle?.source?.subLocation?.name === 'transport'
              ? shortenandHoverString(transportArray.find((ele) => ele.name === colEle?.columnValue)?.label || null, 20, 'auto')
              : shortenandHoverString(colEle?.columnValue, 20, 'auto');
        val =
          (columnEle?.source?.output?.name === 'date_added' || colEle?.type === 'date') && colEle?.columnValue
            ? moment(colEle?.columnValue).format(ansDateForamte)
            : shortenandHoverString(objectValue, 20, 'auto');
      }
    });

    if (!plannedNode && val === null) return null;
    return (
      <>
        {plannedNode}
        {val}
      </>
    );
  };

  const expandTDValues = (columnEle, devEle, expandEle) => {
    const answer = [];
    const rowToMatch = expandEle || devEle;
    let matchedColEle = null;
    detailState?.tableColumData?.forEach((colEle) => {
      if (colEle.columnId === columnEle?._id && matchesPORow(colEle, rowToMatch)) {
        matchedColEle = colEle;
        const checkValue = colEle?.columnValue;

        if (Array.isArray(checkValue)) {
          checkValue.forEach((resultEle) => {
            if (resultEle) {
              if (typeof resultEle === 'object' && !(resultEle instanceof Date)) {
                valueChange(resultEle, answer);
              } else {
                answer.push(resultEle);
              }
            }
          });
        } else if (checkValue && typeof checkValue === 'object' && !(checkValue instanceof Date)) {
          if (columnEle?.source?.output?.name !== 'date_added' || columnEle?.source?.subLocation?.type !== 'date') {
            answer.push(checkValue?.name || checkValue?.keyName || null);
          } else {
            answer.push(checkValue);
          }
        } else if (checkValue) {
          if (columnEle?.source?.subLocation?.name === 'transport') {
            const transportLabel = transportArray?.find((ele) => ele.name === checkValue)?.label || checkValue;
            answer.push(transportLabel);
          } else {
            answer.push(checkValue);
          }
        }
      }
    });

    if (!matchedColEle) {
      return null;
    }

    const isDateColumn = (columnEle?.source?.output?.name === 'date_added' || matchedColEle?.type === 'date') && matchedColEle?.columnValue;
    const displayValue = isDateColumn
      ? moment(matchedColEle.columnValue).format(ansDateForamte)
      : uniqBy(flatMap(answer))
          .filter((e) => e !== '0%' && e !== null && e !== undefined)
          .join(', ') || null;

    return (
      <>
        {matchedColEle?.estimatedDate && (
          <div className={colourCode(matchedColEle)}>
            {t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.CP_PLANNED')}: {moment(matchedColEle?.estimatedDate).format(ansDateForamte)}
          </div>
        )}
        {isDateColumn ? displayValue : shortenandHoverString(displayValue, 20, 'auto')}
      </>
    );
  };

  function renderManualCells(columnElement, i, devEle) {
    const dataArray = detailState?.tableColumData ?? [];
    let parentIndex = -1;
    const relatedData = dataArray.reduce((acc, ele, index) => {
      const isMatch = ele?.columnId === columnElement?._id && matchesPORow(ele, devEle);
      if (isMatch) {
        if (parentIndex === -1) parentIndex = index;
        acc.push(ele);
      }
      return acc;
    }, []);

    if (parentIndex > -1) {
      if (relatedData.length > 0) {
        const verifyValues = relatedData.map((ele) => ele?.columnValue);
        if (hasNoValues(verifyValues)) {
          return renderValues(columnElement, i, devEle, relatedData, parentIndex);
        }

        if (areValuesSimilar(verifyValues)) {
          return renderValues(columnElement, i, devEle, relatedData, parentIndex);
        }

        return renderMultiple();
      }

      return elseRenderValue(dataArray, parentIndex, columnElement, devEle, i, relatedData);
    }

    return elseRenderValue(dataArray, parentIndex, columnElement, devEle, i, relatedData);
  }

  const tdValue = (colEle, devEle) => {
    let answer = null;
    detailState?.tableColumData?.forEach((e) => {
      if (e?.columnId === colEle?._id && matchesPORow(e, devEle)) {
        answer = e;
      }
    });

    if (answer?.columnValue && answer?.columnValue !== null) {
      if (typeof answer?.columnValue === 'object' && !(answer?.columnValue instanceof Date) && !Array.isArray(answer?.columnValue)) {
        for (const key of Object.keys(answer?.columnValue)) {
          if (answer?.columnValue.hasOwnProperty(key) && answer?.columnValue[key] !== false) {
            return answer?.columnValue;
          }
        }
      } else if (Array.isArray(answer?.columnValue)) {
        return answer?.columnValue.join(', ');
      } else {
        return answer?.columnValue;
      }
    }
    return null;
  };

  const expandManualCells = (columnEle, devEle, expandEle, expandIndex, expColInd) => {
    const dataArray = detailState?.tableColumData;
    const rowToMatch = expandEle || devEle;

    const findex = dataArray?.findIndex((e) => e?.columnId === columnEle?._id && matchesPORow(e, rowToMatch));

    if (findex !== -1) {
      return (
        <>
          {dataArray?.[findex]?.estimatedDate && (
            <div className={`${colourCode(dataArray?.[findex])} f-12 text-bold`}>
              {plannedWithcolon}
              {moment.isMoment(dataArray?.[findex]?.estimatedDate)
                ? dataArray?.[findex]?.estimatedDate.format(ansDateForamte)
                : moment(dataArray?.[findex]?.estimatedDate).format(ansDateForamte)}
            </div>
          )}
          {dataArray?.[findex]?.durationDays && dataArray?.[findex]?.columnValue && (
            <div className="d-flex w-100">
              <span className={`${colourCodeForDurationDate(dataArray?.[findex])} linkedColour duration-counter`}>
                {getDateDifference(dataArray?.[findex]?.durationDays, dataArray?.[findex]?.columnValue)}
              </span>
            </div>
          )}
          <RenderingManualItems
            {...columnEle}
            keyName={columnEle?.manualType?.key}
            keyprop={`${columnEle?.manualType?.key}_${columnEle?._id}_${rowToMatch?.rowKey}_${dataArray?.[findex]?.estimatedDate || 'no-date'}`}
            value={dataArray?.[findex]?.columnValue || null}
            placeholder={t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.ENTER_VALUE')}
            disableCondition={disablingConditions(columnEle) || bulkUpdate}
            change={(e) => changeManualInputs(e, columnEle, devEle, expandEle || null, expandIndex !== undefined)}
            options={columnEle?.options}
            characterLimit={columnEle?.characterLimit}
            labelColours={columnEle?.labelColoursOptions}
            subType={columnEle?.manualType?.subType}
            status={columnEle?.status}
            onCalendarOpen={() => callDatePickerApi(columnEle?._id, expandIndex !== undefined ? expandIndex : devEle?._id, rowToMatch?.rowKey, expColInd)}
            onCalendarClose={() => onCalendarClose()}
            onMenuOpen={() => callDatePickerApi(columnEle?._id, expandIndex !== undefined ? expandIndex : devEle?._id, rowToMatch?.rowKey, expColInd)}
            onMenuClose={() => onCalendarClose()}
          />
        </>
      );
    }

    return (
      <RenderingManualItems
        {...columnEle}
        keyName={columnEle?.manualType?.key}
        keyprop={`${columnEle?.manualType?.key}_${columnEle?._id}_${rowToMatch?.rowKey}`}
        value={null}
        placeholder={t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.ENTER_VALUE')}
        disableCondition={disablingConditions(columnEle) || bulkUpdate}
        change={(e) => changeManualInputs(e, columnEle, devEle, expandEle || null, expandIndex !== undefined)}
        options={columnEle?.options}
        characterLimit={columnEle?.characterLimit}
        labelColours={columnEle?.labelColoursOptions}
        subType={columnEle?.manualType?.subType}
        status={columnEle?.status}
        onCalendarOpen={() => callDatePickerApi(columnEle?._id, expandIndex !== undefined ? expandIndex : devEle?._id, rowToMatch?.rowKey, expColInd)}
        onCalendarClose={() => onCalendarClose()}
        onMenuOpen={() => callDatePickerApi(columnEle?._id, expandIndex !== undefined ? expandIndex : devEle?._id, rowToMatch?.rowKey, expColInd)}
        onMenuClose={() => onCalendarClose()}
      />
    );
  };

  const renderSourceTd = (columnElement, columnIndex, devEle) => {
    return (
      <div
        key={`source_${columnElement?._id}_${columnIndex}_${devEle?.rowKey}`}
        className={((columnElement?.isRequired && !tdValue(columnElement, devEle)) || colourClassName(columnElement, devEle)) && cellClassName}
      >
        <div
          className={`position-relative  ${
            (columnElement?.isRequired && !tdValue(columnElement, devEle) && `cell-red ${color}`) ||
            (colourClassName(columnElement, devEle) && `${colourClassName(columnElement, devEle)} ${color}`)
          }`}
        >
          {findResultValue(columnElement, devEle?.rowKey)}
        </div>
      </div>
    );
  };

  const addClassName = (ind, isPinned) => {
    if (isPinned) {
      return `${selectedPinnedColumns?.length - 1 === ind ? 'first-fix-col w-224px' : 'w-224px'} `;
    }
    return 'w-224px';
  };

  const tdValueForExpand = (colEle, expandEle) => {
    let answer = null;
    detailState?.tableColumData?.forEach((e) => {
      if (e?.columnId === colEle?._id && matchesPORow(e, expandEle)) {
        answer = e;
      }
    });

    if (answer?.columnValue && answer?.columnValue !== null) {
      if (typeof answer?.columnValue === 'object' && !(answer?.columnValue instanceof Date) && !Array.isArray(answer?.columnValue)) {
        for (const key of Object.keys(answer?.columnValue)) {
          if (answer?.columnValue.hasOwnProperty(key) && answer?.columnValue[key] !== false) {
            return answer?.columnValue;
          }
        }
      } else if (Array.isArray(answer?.columnValue)) {
        return answer?.columnValue.join(', ');
      } else {
        return answer?.columnValue;
      }
    }
    return null;
  };

  const colourClassNameForExpand = (colEle, expandEle) => {
    if (!colEle?.isColourCodingActive) return;

    const verifyAgainst = getVerifyAgainstValueForExpand(colEle, expandEle);
    if (!verifyAgainst) return 'deleted';

    const colourCoding = colEle?.colourCoding;
    const colourType = getColourType(colEle);
    return getColourClass(colourCoding, verifyAgainst, colourType, expandEle, expandEle);
  };

  const getVerifyAgainstValueForExpand = (colEle, expandEle) => {
    const copyData = detailState?.tableColumData ?? [];
    let mainObject = null;

    copyData.forEach((mainDEle) => {
      if (mainDEle.columnId === colEle?._id && matchesPORow(mainDEle, expandEle) && mainDEle.columnValue) {
        mainObject = mainDEle;
      }
    });

    return mainObject?.columnValue;
  };

  const renderExpandedManualTd = (expCol, expColInd, devEle, expandEle, expandIndex) => {
    return (
      <td
        key={`manual_${expCol?._id}_${expColInd}_${expandEle?.rowKey}`}
        className={`${addClassName(expColInd, expCol.isPinned)} ${
          (expCol?.isRequired && !tdValueForExpand(expCol, expandEle)) || colourClassNameForExpand(expCol, expandEle) ? cellClassName : ''
        } ${checkZindex(`${expCol?._id}_${expandIndex}_${expandEle?.rowKey}_${expColInd}`)}`}
        onClick={() => {
          if (!copyValue) {
            clickedForCopyingExpand(expCol, expandEle);
          }
        }}
      >
        <div
          className={`position-relative ${
            copyValueClass(expCol, devEle, expandEle)
              ? `${color} ${copyValueClass(expCol, devEle, expandEle)}`
              : (expCol?.isRequired && !tdValueForExpand(expCol, expandEle) && `cell-red ${color}`) || `${colourClassNameForExpand(expCol, expandEle)} ${color}`
          }`}
        >
          {expandManualCells(expCol, devEle, expandEle, expandIndex, expColInd)}
        </div>
      </td>
    );
  };

  const expandedTD = (expCol, expColInd, devEle, expandEle, expandIndex) => {
    return (
      <td
        key={`source_${expCol?._id}_${expColInd}_${expandEle?.rowKey}`}
        className={`${addClassName(expColInd, expCol.isPinned)} ${
          (expCol?.isRequired && !tdValueForExpand(expCol, expandEle)) || colourClassNameForExpand(expCol, expandEle) ? cellClassName : ''
        } ${checkZindex(`${expCol?._id}_${expandIndex}_${expandEle?.rowKey}`)}`}
        onClick={() => {
          if (!copyValue) {
            clickedForCopyingExpand(expCol, expandEle);
          }
        }}
      >
        <div
          className={`position-relative ${
            copyValueClass(expCol, devEle, expandEle)
              ? `${color} ${copyValueClass(expCol, devEle, expandEle)}`
              : (expCol?.isRequired && !tdValueForExpand(expCol, expandEle) && `cell-red ${color}`) ||
                (colourClassNameForExpand(expCol, expandEle) && `${colourClassNameForExpand(expCol, expandEle)} ${color}`)
          }`}
        >
          {expandTDValues(expCol, devEle, expandEle)}
        </div>
      </td>
    );
  };

  const renderExpandedCell = (columnElement, columnIndex, devEle, expandEle, expandIndex, isPinned) => {
    const isPOStaticPinned = PO_STATIC_PINNABLE_IDS.includes(columnElement?._id);
    if (!isPOStaticPinned && !showColumn(columnElement)) return null;
    if (columnElement?._id === 'colorway') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)}`}>{shortenandHoverString(expandEle?.colourwayName || expandEle?.colourWayName, 20, 'auto')}</td>
      );
    }
    if (columnElement?._id === 'supplier') {
      return <td className={`${addClassName(columnIndex, isPinned)}`}>{shortenandHoverString(expandEle?.supplierNames, 20, 'auto')}</td>;
    }
    if (columnElement?._id === 'factory') {
      return <td className={`${addClassName(columnIndex, isPinned)}`}>{shortenandHoverString(expandEle?.supplierFactories, 20, 'auto')}</td>;
    }
    if (columnElement?._id === 'skuNumber') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(expandEle?.skuNumber ?? null, 20, 'auto')}</span>
        </td>
      );
    }
    if (columnElement?._id === 'transport') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>
            {expandEle?.transport
              ? shortenandHoverString(transportArray.find((ele) => ele.name === expandEle.transport)?.label ?? expandEle.transport, 20, 'auto')
              : null}
          </span>
        </td>
      );
    }
    if (columnElement?._id === 'size') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(expandEle?.sizeName ?? expandEle?.sizeId ?? null, 20, 'auto')}</span>
        </td>
      );
    }
    if (columnElement?._id === 'location') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(expandEle?.location ?? null, 20, 'auto')}</span>
        </td>
      );
    }
    if (columnElement?._id === 'quantity') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(expandEle?.quantity ?? null, 20, 'auto')}</span>
        </td>
      );
    }
    if (columnElement?._id === 'approvedByDate') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(expandEle?.approvedByDate ?? null, 20, 'auto')}</span>
        </td>
      );
    }
    if (columnElement?._id === 'exFactoryDate') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(expandEle?.exFactoryDate ?? null, 20, 'auto')}</span>
        </td>
      );
    }
    if (columnElement?._id === 'shipmentDate') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(expandEle?.shipmentDate ?? null, 20, 'auto')}</span>
        </td>
      );
    }

    return columnElement?.columnType === 'source'
      ? expandedTD(columnElement, columnIndex, devEle, expandEle, expandIndex)
      : renderExpandedManualTd(columnElement, columnIndex, devEle, expandEle, expandIndex);
  };

  const clickedForCopying = (columnElement, devEle) => {
    if (bulkUpdate) {
      const dataArray = detailState?.tableColumData ?? [];
      const relatedData = dataArray?.find((datEle) => datEle?.columnId === columnElement?._id && matchesPORow(datEle, devEle));
      if (relatedData?.columnValue) {
        setCopyValue(relatedData);
      } else {
        toasterMsg(t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.NO_DATA_TO_COPY'), 'danger');
      }
    }
  };

  const clickedForCopyingExpand = (columnElement, expandEle) => {
    if (bulkUpdate) {
      const dataArray = detailState?.tableColumData ?? [];
      const relatedData = dataArray?.find((datEle) => datEle?.columnId === columnElement?._id && matchesPORow(datEle, expandEle));
      if (relatedData?.columnValue) {
        setCopyValue(relatedData);
      } else {
        toasterMsg(t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.NO_DATA_TO_COPY'), 'danger');
      }
    }
  };

  const copyValueClass = (columnElement, devEle, expandEle = null) => {
    if (copyValue && copyValue?.columnId === columnElement?._id) {
      if (expandEle && copyValue?.rowKey === expandEle?.rowKey) {
        return 'copy-value';
      }
      if (!expandEle && copyValue?.rowKey === devEle?.rowKey) {
        return 'copy-value';
      }
    }
  };

  const getUniqueMonthsFromSelectedYears = (selectedYears, originalData) => {
    const monthSet = new Set();
    selectedYears.forEach(({ value: year }) => {
      const months = originalData[year];
      if (months) {
        Object.keys(months).forEach((month) => {
          monthSet.add(month);
        });
      }
    });
    const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return Array.from(monthSet).sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));
  };

  const fetchFilterData = async (columnData, filterType) => {
    const isSpecialColumn = SPECIAL_COLUMNS_CONFIG[columnData?._id];
    const columnTypes = columnData?.manualType?.key === 'Date_picker' ? 'date' : 'other';
    let objFilter = {
      pcpId,
      columnId: columnData?._id,
      columnType: columnData?.columnType === 'source' ? 'source' : columnTypes,
      manualType: columnData?.manualType?.key,
      timeZone: Intl.DateTimeFormat().resolvedOptions()?.timeZone,
      fetchDetailFor: filterType || 'actual',
      subLocation: columnData?.source?.subLocation?.name,
      searchText: detailState?.searchText?.trim(),
      isPOBased: true,
    };

    if (isSpecialColumn) {
      objFilter.isSpecialColumn = true;
      objFilter.dbField = isSpecialColumn.dbField;
      objFilter.hasNotFilled = isSpecialColumn.hasNotFilled;
    }
    if (detailState?.filterData) {
      const activeFilters = Object.entries(detailState.filterData).reduce((acc, [key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          acc[key] = value;
        }
        return acc;
      }, {});

      if (Object.keys(activeFilters).length > 0) {
        objFilter = {
          ...objFilter,
          ...activeFilters,
        };
      }
    }
    if (productCpColumnTabs?.allProductIds?.length > 0) {
      objFilter.allProductIds = productCpColumnTabs?.allProductIds;
    }
    if (productCpColumnTabs?.filteredProductIds?.length > 0) {
      objFilter.filteredProductIds = productCpColumnTabs?.filteredProductIds;
    }
    setColumnOptionLoader(true);
    const filterData = await dispatch(action.getColumFilterData(objFilter));
    if (filterData?.code === 200) {
      const colFilterOriginalData = filterData?.data;
      let objData = null;
      switch (columnData?.manualType?.key) {
        case 'Date_picker':
          {
            const years = Object.keys(colFilterOriginalData);
            const yearOptions = createOptions(years, 'label', 'value', true);
            const uniqueMonths = getUniqueMonthsFromSelectedYears(yearOptions, colFilterOriginalData);
            objData = {
              yearOptions,
              filterType,
              ...(yearOptions?.length === 1 && { selectedYear: yearOptions }),
              ...(yearOptions?.length === 1 && {
                monthOption: uniqueMonths.map((month) => ({
                  value: month,
                  label: month,
                })),
              }),
              ...(uniqueMonths?.length === 1 && {
                selectedMonth: [{ value: uniqueMonths?.[0], label: uniqueMonths?.[0] }],
              }),
            };
          }
          break;

        case 'Number_Integer':
          objData = {
            textOptions: colFilterOriginalData
              .filter((val) => val !== 'Not Filled')
              .map((val) => ({
                label: typeof val === 'number' ? val.toString() : val,
                value: val,
              })),
            textFilterData: colFilterOriginalData
              .filter((val) => val !== 'Not Filled')
              .map((val) => ({
                label: typeof val === 'number' ? val.toString() : val,
                value: val,
              })),
            searchText: '',
          };
          break;

        default:
          objData = {
            textOptions: createOptions(colFilterOriginalData, 'label', 'value', true),
            textFilterData: createOptions(colFilterOriginalData, 'label', 'value', true),
            searchText: '',
          };
          break;
      }
      if (!columnsFilterData.some((item) => item.columnId === columnData?._id)) {
        setColumnsFilterData([
          ...columnsFilterData,
          {
            columnId: columnData?._id,
            originalData: colFilterOriginalData,
            ...objData,
          },
        ]);
      } else {
        setColumnsFilterData((prev) =>
          prev.map((item) => {
            if (item.columnId === columnData?._id) {
              if (!item?.yearOptions?.length) {
                return {
                  columnId: columnData?._id,
                  originalData: colFilterOriginalData,
                  selectedValues: item?.selectedValues,
                  filterApplied: item?.filterApplied,
                  ...objData,
                };
              }
              return {
                ...item,
                originalData: colFilterOriginalData,
                ...objData,
              };
            }
            return item;
          }),
        );
      }
    }
    setColumnOptionLoader(false);
  };

  const handleSelect = (value, columnId, fieldName) => {
    setColumnsFilterData((prev) => {
      return prev?.map((obj) => {
        const uniqueMonths = getUniqueMonthsFromSelectedYears(value, obj?.originalData);
        if (obj?.columnId === columnId) {
          switch (fieldName) {
            case 'selectedYear':
              return {
                ...obj,
                [fieldName]: value,
                monthOption: uniqueMonths.map((month) => ({
                  value: month,
                  label: month,
                })),
                ...(uniqueMonths?.length === 1 && {
                  selectedMonth: [{ value: uniqueMonths?.[0], label: uniqueMonths?.[0] }],
                }),
              };
            case 'selectedMonth':
              return {
                ...obj,
                [fieldName]: value,
              };
            default:
              return obj;
          }
        }
        return obj;
      });
    });
  };

  const ascDesc = () => {
    return detailState?.sortData?.sortOrder === 'asc' ? <i className="icon-Sort-CP-asc f-24" /> : <i className="icon-Sort-CP-desc f-24" />;
  };
  const iconsChange = () => {
    return !detailState?.sortData?.sortBy ? <i className="icon-Sort-CP f-24" /> : ascDesc();
  };

  const handleSortBy = (propertyName) => {
    if (propertyName === 'number') {
      setDetailState((prev) => ({
        ...prev,
        sortData: {
          sortAt: 'ProductInfo',
          sortBy: propertyName,
          sortOrder: prev?.sortData?.sortOrder === 'asc' ? 'desc' : 'asc',
        },
      }));
    } else {
      const dateType = detailState?.columnsTab?.find((obj) => obj._id === propertyName)?.manualType?.key === 'Date_picker';
      setDetailState((prev) => ({
        ...prev,
        sortData: {
          sortAt: dateType ? 'columnData' : 'textType',
          sortBy: propertyName,
          sortOrder: prev?.sortData?.sortOrder === 'asc' ? 'desc' : 'asc',
        },
      }));
    }
  };

  const supplierConditions = (columnElement, durationValue, finalCalculaions, showSortingIcons, isManualColumn, columnStateData, isPinned, ind) => {
    return (
      <th className={addClassName(ind, isPinned)} key={columnElement?._id}>
        <div className="d-flex align-items-center">
          <OverlayTrigger
            placement="top"
            trigger="click"
            rootClose
            show={activeColumnId === columnElement?._id}
            onToggle={() => setActiveColumnId(null)}
            overlay={
              <Popover className="custom-popover cp-popover">
                <Popover.Content>
                  <div className="progress-container">
                    <div className="position-relative">
                      <div className="progress-label">
                        <b>{`${t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.ON_TIME')}: `} </b>{' '}
                        <span className="progress-value"> {durationValue?.onTime || 0}%</span>
                      </div>
                      <ProgressBar
                        className={`product-completion ${durationValue?.onTime && (parseFloat(durationValue?.onTime) ? 'green-bar' : '')}`}
                        now={durationValue?.onTime || 0}
                      />
                    </div>
                    <div className="position-relative">
                      <div className="progress-label">
                        <b>{`${t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.LATE')}: `}</b>{' '}
                        <span className="progress-value"> {durationValue?.delayed || 0}%</span>
                      </div>
                      <ProgressBar
                        className={`product-completion ${durationValue?.delayed && (parseFloat(durationValue?.delayed) ? 'red-bar' : '')}`}
                        now={durationValue?.delayed || 0}
                      />
                    </div>
                    <div className="position-relative">
                      <div className="progress-label">
                        <b>{`${t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.PENDING')}: `}</b>
                        <span className="progress-value"> {durationValue?.pending || 0}%</span>
                      </div>
                      <ProgressBar
                        className={`product-completion ${durationValue?.pending && (parseFloat(durationValue?.pending) ? 'amber-bar' : '')}`}
                        now={durationValue?.pending || 0}
                      />
                    </div>
                  </div>
                </Popover.Content>
              </Popover>
            }
          >
            <div className="flex-fill overflow-hidden" onClick={() => handleTogglePopover(columnElement?._id)}>
              {columnElement?.isRequired && 100 - (parseFloat(finalCalculaions || 0) || 0) !== 0 ? (
                <div className="position-relative cp-columns">
                  <div className="progress-container">
                    <span className={`remaining-percentage ${parseFloat(finalCalculaions || 0) > 80 ? 'low-percentage' : ''}`}>
                      {parseFloat(finalCalculaions || 0)} %
                    </span>
                    <ProgressBar
                      className={`product-completion ${parseFloat(finalCalculaions || 0) < 50 ? 'red-bar' : 'amber-bar'}`}
                      now={100 - (finalCalculaions || 0)}
                    />
                    <span className="name-label truncate-text" title={columnElement?.name}>
                      {columnElement?.name}
                    </span>
                  </div>
                </div>
              ) : (
                (
                  <span className="name-label truncate-text" title={columnElement?.name}>
                    {columnElement?.name}
                  </span>
                ) || null
              )}
            </div>
          </OverlayTrigger>
          {showSortingIcons && (
            <button
              type="button"
              className="btn d-inline-flex p-4pix cursor-pointer"
              onClick={() => handleSortBy(columnElement?._id)}
              disabled={
                !detailState?.tableColumData?.some((e) => e?.columnId === columnElement?._id && e?.columnValue) ||
                detailState?.tableColumData?.length === 0 ||
                detailState?.loading ||
                detailState?.isMainLoading ||
                detailState?.tableLoading
              }
            >
              {detailState?.sortData?.sortBy === columnElement?._id ? <>{iconsChange()}</> : <i className="icon-Sort-CP f-24" />}
            </button>
          )}
          {isManualColumn && (
            <ColumnFilter
              pcpId={pcpId}
              columnStateData={columnStateData}
              setColumnsFilterData={setColumnsFilterData}
              columnElement={columnElement}
              handleSelect={handleSelect}
              fetchFilterData={fetchFilterData}
              allProductIds={productCpColumnTabs?.allProductIds}
              filteredProductIds={productCpColumnTabs?.filteredProductIds}
            />
          )}
        </div>
      </th>
    );
  };

  const renderColumn = (columnElement, ind, isPinned) => {
    const columnStateData = columnsFilterData?.find((obj) => obj?.columnId === columnElement?._id);
    if (PO_STATIC_PINNABLE_IDS.includes(columnElement?._id) && detailState?.selectedColumnShow?.includes(columnElement?._id)) {
      return (
        <th className={addClassName(ind, isPinned)} key={columnElement?._id}>
          <p className="mb-0 d-flex align-items-center">
            <span className="truncate-text name-label flex-fill overflow-hidden" title={columnElement?.name}>
              {columnElement?.name ?? null}
            </span>
            {['location', 'quantity', 'approvedByDate', 'exFactoryDate', 'shipmentDate'].includes(columnElement?._id) && (
              <ColumnFilter
                pcpId={pcpId}
                columnStateData={columnStateData}
                setColumnsFilterData={setColumnsFilterData}
                columnElement={columnElement}
                handleSelect={handleSelect}
                fetchFilterData={fetchFilterData}
                columnOptionLoader={columnOptionLoader}
                allProductIds={productCpColumnTabs?.allProductIds}
                filteredProductIds={productCpColumnTabs?.filteredProductIds}
              />
            )}
          </p>
        </th>
      );
    }
    if (!showColumn(columnElement)) return null;
    let durationValue = detailState?.finalPercentageCalculation?.find((obj) => obj[columnElement._id]);
    durationValue = durationValue?.[columnElement._id];
    let finalCalculaions = detailState?.finalCalculaions?.find((obj) => obj[columnElement?._id]);
    finalCalculaions = finalCalculaions?.[columnElement._id];
    const isManualColumn =
      (columnElement?.columnType === 'manual' && hasFilterPermission.includes(columnElement?.manualType?.key)) ||
      (columnElement?.source?.location?.name === 'Product_Information' &&
        !['size', 'gradingGroup', 'length', 'colourwayName', 'colourDesc', 'colourwayRef', 'colourGroup', 'colourRef'].includes(
          columnElement?.source?.subLocation?.name,
        ));
    const showSortingIcons =
      columnElement?.columnType === 'manual' && [('Date_picker', 'Number', 'Number_percentage', 'Textbox')]?.includes(columnElement?.manualType?.key);
    return isSupplierLoggedIn ? (
      supplierConditions(columnElement, durationValue, finalCalculaions, showSortingIcons, isManualColumn, columnStateData, isPinned, ind)
    ) : (
      <th className={addClassName(ind, isPinned)} key={columnElement?._id} style={{ cursor: 'pointer' }}>
        <div className="d-flex align-items-center">
          <OverlayTrigger
            placement="top"
            trigger="click"
            rootClose
            show={activeColumnId === columnElement?._id}
            onToggle={() => setActiveColumnId(null)}
            overlay={
              <Popover className="custom-popover cp-popover">
                <Popover.Content>
                  {columnElement?.dependentObj?.durationValue && (
                    <div className="progress-container">
                      <div className="position-relative">
                        <div className="progress-label">
                          <b>
                            {`${t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.PENDING')}:`}
                            &nbsp;
                          </b>
                          <span className="progress-value"> {durationValue?.pending || 0}%</span>
                        </div>
                        <ProgressBar className={`product-completion ${parseFloat(durationValue?.pending) ? 'amber-bar' : ''}`} now={durationValue?.pending} />
                      </div>
                      <div className="position-relative">
                        <div className="progress-label">
                          <b>
                            {`${t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.ON_TIME')}:`}
                            &nbsp;
                          </b>
                          <span className="progress-value">{durationValue?.onTime || 0}%</span>
                        </div>
                        <ProgressBar
                          className={`product-completion ${parseFloat(durationValue?.onTime || 0) ? 'green-bar' : ''}`}
                          now={durationValue?.onTime}
                        />
                      </div>
                      <div className="position-relative">
                        <div className="progress-label">
                          <b>
                            {`${t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.LATE')}:`}
                            &nbsp;
                          </b>
                          <span className="progress-value">{durationValue?.delayed || 0}%</span>
                        </div>
                        <ProgressBar
                          className={`product-completion ${parseFloat(durationValue?.delayed || 0) ? 'red-bar' : ''}`}
                          now={durationValue?.delayed}
                        />
                      </div>

                      <div className="d-flex align-items-center">
                        <span className="text-bold f-12 mr-2">{t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SHOW_ONLY')}</span>
                        <p className="custom-radio custom-radio-cp mb-0 mr-3">
                          <input
                            type="radio"
                            id={`onTime_${columnElement?._id}`}
                            name={`dependentFilter_${columnElement?._id}`}
                            checked={dependentFilter[columnElement?._id] === 'onTime'}
                            className="custom-radio-input-cp"
                            onClick={(e) => {
                              const currentValue = dependentFilter[columnElement?._id];
                              if (currentValue === 'onTime') {
                                e.preventDefault();
                                const updatedFilter = { ...dependentFilter };
                                delete updatedFilter[columnElement?._id];
                                setDependentFilter(updatedFilter);
                              }
                            }}
                            onChange={() => {
                              setDependentFilter({
                                ...dependentFilter,
                                [columnElement?._id]: 'onTime',
                              });
                            }}
                          />
                          <label className="mb-0 f-12" htmlFor={`onTime_${columnElement?._id}`}>
                            {t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.ON_TIME')}
                          </label>
                        </p>
                        <p className="custom-radio custom-radio-cp mb-0">
                          <input
                            type="radio"
                            id={`late_${columnElement?._id}`}
                            className="custom-radio-input-cp"
                            name={`dependentFilter_${columnElement?._id}`}
                            checked={dependentFilter[columnElement?._id] === 'late'}
                            onClick={(e) => {
                              const currentValue = dependentFilter[columnElement?._id];
                              if (currentValue === 'late') {
                                e.preventDefault();
                                const updatedFilter = { ...dependentFilter };
                                delete updatedFilter[columnElement?._id];
                                setDependentFilter(updatedFilter);
                              }
                            }}
                            onChange={() => {
                              setDependentFilter({
                                ...dependentFilter,
                                [columnElement?._id]: 'late',
                              });
                            }}
                          />
                          <label className="mb-0 f-12" htmlFor={`late_${columnElement?._id}`}>
                            {t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.LATE')}
                          </label>
                        </p>
                      </div>

                      <div>
                        <b className="f-12">{t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.DEPENDENTS')}</b>
                        <p className="mb-0 f-12">
                          <span className="text-bold">{columnElement?.dependentObj?.futureDependent ? 'After' : 'Before'}: &nbsp;:</span>
                          {columnElement?.dependentObj?.dependentColumn?.name}
                        </p>
                        <p className="mb-0 f-12">
                          <span className="text-bold">Duration: </span>
                          {columnElement?.dependentObj?.durationValue} {columnElement?.dependentObj?.durationUnit?.label}
                        </p>
                      </div>
                    </div>
                  )}
                  {!isSupplierLoggedIn && (
                    <div className="assignee-text">
                      {t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.ASSIGNEE')}:{' '}
                      {[...userGroupsCardList, ...supplierList]
                        ?.filter((cEle) => columnElement?.assignee?.includes(cEle?._id))
                        ?.map((e) => e?.name)
                        .join(', ')}
                    </div>
                  )}
                </Popover.Content>
              </Popover>
            }
          >
            <div className="flex-fill overflow-hidden" onClick={() => handleTogglePopover(columnElement?._id)}>
              {columnElement?.isRequired && 100 - (parseFloat(finalCalculaions || 0) || 0) !== 0 ? (
                <div className="position-relative cp-columns">
                  <div className="progress-container">
                    <span className={`remaining-percentage ${parseFloat(finalCalculaions || 0) > 80 ? 'low-percentage' : ''}`}>
                      {parseFloat(finalCalculaions || 0)} %
                    </span>
                    <ProgressBar
                      className={`product-completion ${parseFloat(finalCalculaions || 0) < 50 ? 'red-bar' : 'amber-bar'}`}
                      now={100 - (finalCalculaions || 0)}
                    />
                    <span className="name-label truncate-text" title={columnElement?.name}>
                      {columnElement?.name || null}
                    </span>
                  </div>
                </div>
              ) : (
                <span className="name-label truncate-text" title={columnElement?.name}>
                  {columnElement?.name || null}
                </span>
              )}
            </div>
          </OverlayTrigger>
          {showSortingIcons && (
            <button
              type="button"
              className="btn d-inline-flex p-4pix cursor-pointer"
              onClick={() => handleSortBy(columnElement?._id)}
              disabled={
                !detailState?.tableColumData?.some((e) => e?.columnId === columnElement?._id && e?.columnValue) ||
                detailState?.tableColumData?.length === 0 ||
                detailState?.loading ||
                detailState?.isMainLoading ||
                detailState?.tableLoading
              }
            >
              {detailState?.sortData?.sortBy === columnElement?._id ? <>{iconsChange()}</> : <i className="icon-Sort-CP f-24" />}
            </button>
          )}
          {isManualColumn && (
            <ColumnFilter
              pcpId={pcpId}
              columnStateData={columnStateData}
              setColumnsFilterData={setColumnsFilterData}
              columnElement={columnElement}
              handleSelect={handleSelect}
              fetchFilterData={fetchFilterData}
              columnOptionLoader={columnOptionLoader}
              allProductIds={productCpColumnTabs?.allProductIds}
              filteredProductIds={productCpColumnTabs?.filteredProductIds}
            />
          )}
        </div>
      </th>
    );
  };

  const renderSpecialColumnHeader = (columnId, isPinned, ind) => {
    const columnConfig = SPECIAL_COLUMNS_CONFIG[columnId];
    if (!columnConfig) return null;

    const columnStateData = columnsFilterData?.find((obj) => obj?.columnId === columnId);

    return (
      <th
        key={`${columnId}-${ind}`}
        className={`w-170px ${columnId === 'productName' && !detailState?.selectedPinnedColumns?.length ? 'first-fix-col' : ''} ${
          isPinned ? 'pinned-column' : ''
        }`}
      >
        <div className="d-flex align-items-center">
          <div className="flex-fill overflow-hidden">
            <span className="name-label truncate-text" title={columnConfig?.name}>
              {columnConfig?.name || null}
            </span>
          </div>
          <ColumnFilter
            pcpId={pcpId}
            columnStateData={columnStateData}
            setColumnsFilterData={setColumnsFilterData}
            columnElement={columnConfig}
            handleSelect={handleSelect}
            fetchFilterData={fetchFilterData}
            columnOptionLoader={columnOptionLoader}
            allProductIds={productCpColumnTabs?.allProductIds}
            filteredProductIds={productCpColumnTabs?.filteredProductIds}
            maxSelections={25}
          />
        </div>
      </th>
    );
  };

  const renderCell = (columnElement, columnIndex, devEle, isPinned) => {
    if (!showColumn(columnElement)) return null;

    if (columnElement?._id === 'colorway') {
      return <td className={`${addClassName(columnIndex, isPinned)}`}>{shortenandHoverString(devEle?.colourwayName || devEle?.colourWayName, 20, 'auto')}</td>;
    }
    if (columnElement?._id === 'supplier') {
      return <td className={`${addClassName(columnIndex, isPinned)}`}>{shortenandHoverString(devEle?.supplierNames, 20, 'auto')}</td>;
    }
    if (columnElement?._id === 'factory') {
      return <td className={`${addClassName(columnIndex, isPinned)}`}>{shortenandHoverString(devEle?.supplierFactories, 20, 'auto')}</td>;
    }
    if (columnElement?._id === 'skuNumber') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(devEle?.skuNumber ?? null, 20, 'auto')}</span>
        </td>
      );
    }
    if (columnElement?._id === 'transport') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>
            {devEle?.transport
              ? shortenandHoverString(transportArray.find((ele) => ele.name === devEle.transport)?.label ?? devEle.transport, 20, 'auto')
              : null}
          </span>
        </td>
      );
    }
    if (columnElement?._id === 'size') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(devEle?.sizeName ?? devEle?.sizeId ?? null, 20, 'auto')}</span>
        </td>
      );
    }
    if (columnElement?._id === 'location') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(devEle?.location ?? null, 20, 'auto')}</span>
        </td>
      );
    }
    if (columnElement?._id === 'quantity') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(devEle?.quantity ?? null, 20, 'auto')}</span>
        </td>
      );
    }
    if (columnElement?._id === 'approvedByDate') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(devEle?.approvedByDate ?? null, 20, 'auto')}</span>
        </td>
      );
    }
    if (columnElement?._id === 'exFactoryDate') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(devEle?.exFactoryDate ?? null, 20, 'auto')}</span>
        </td>
      );
    }
    if (columnElement?._id === 'shipmentDate') {
      return (
        <td className={`${addClassName(columnIndex, isPinned)} w-170px`}>
          <span>{shortenandHoverString(devEle?.shipmentDate ?? null, 20, 'auto')}</span>
        </td>
      );
    }
    return (
      <td
        key={`${columnElement?._id}_${columnIndex}_${devEle?.rowKey}`}
        className={`${addClassName(columnIndex, isPinned)} ${
          (columnElement?.isRequired && !tdValue(columnElement, devEle)) || colourClassName(columnElement, devEle) ? cellClassName : ''
        } ${checkZindex(`${columnElement?._id}_${columnIndex}_${devEle?.rowKey}`)}`}
        onClick={() => {
          if (!copyValue) {
            clickedForCopying(columnElement, devEle);
          }
        }}
      >
        <div
          className={`position-relative ${
            copyValueClass(columnElement, devEle)
              ? `${color} ${copyValueClass(columnElement, devEle)}`
              : (columnElement?.isRequired && !tdValue(columnElement, devEle) && `cell-red ${color}`) || `${colourClassName(columnElement, devEle)} ${color}`
          }`}
        >
          {columnElement?.columnType === 'source' ? renderSourceTd(columnElement, columnIndex, devEle) : renderManualCells(columnElement, columnIndex, devEle)}
        </div>
      </td>
    );
  };

  const safeHandleNextPage = useCallback(() => {
    if (isExpandingRef.current || detailState?.tableLoading || detailState?.loading || !detailState?.hasMore) {
      return;
    }
    handleNextPageInScroll();
  }, [detailState?.hasMore, detailState?.tableLoading, detailState?.loading, handleNextPageInScroll]);

  const renderMaintable = () => {
    if (
      isEmpty(detailState?.tableProductDetail) &&
      !detailState?.loading &&
      !detailState?.isMainLoading &&
      !detailState?.tableLoading &&
      !detailState?.sortData?.sortBy &&
      !columnsFilterData?.length > 0
    ) {
      const isPOBased = individualDetail?.basedOnPO === true;
      const emptyMessageKey = isPOBased
        ? 'PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.NO_PO_MATCHED_PRODUCT_CRITICAL_PATH'
        : 'PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.PRODUCT_CRITICAL_PATH';

      return (
        <Container fluid className="py-4">
          <Row>
            <Col md="12">
              <p className="alert alert-info override-alert-info justify-content-center align-item-center d-flex">{t(emptyMessageKey)}</p>
            </Col>
          </Row>
        </Container>
      );
    }

    return (
      <div
        id="CPTableContainer"
        className="cp-scroll-container"
        style={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: '75vh',
        }}
      >
        <InfiniteScroll
          dataLength={detailState?.tableProductDetail?.length}
          loader={detailState?.tableLoading && <Loader />}
          hasMore={detailState?.hasMore && !isExpandingRef.current}
          next={safeHandleNextPage}
          className="fixed-col-container cp-table-container"
          scrollableTarget="CPTableContainer"
        >
          <table id="ProductCPPOTable" className={`table common-custom-table fixed-header mb-0 ${pinedColumLength()}`}>
            <thead>
              <tr>
                {!isProductArchived && (
                  <th className="w-50px">
                    <div className="common-checkbox margin-auto">
                      <input
                        type="checkbox"
                        onChange={handleSelectAllColumnOfTabs}
                        name={`prod_${detailState.tabId}`}
                        id={`prod_${detailState.tabId}`}
                        checked={detailState.selectAllColumns}
                      />
                      <label htmlFor={`prod_${detailState.tabId}`} className="chklabel" />
                    </div>
                  </th>
                )}
                <th className="w-50px">
                  <button
                    type="button"
                    className="btn-up-down"
                    onClick={() => {
                      isExpandingRef.current = true;
                      expandAllProductInfo(!detailState.expandAllColumns);
                      setTimeout(() => {
                        isExpandingRef.current = false;
                      }, 500);
                    }}
                    aria-label={t(detailState.expandAllColumns ? 'COMMON_LABEL.EXPAND' : 'COMMON_LABEL.COLLAPSE')}
                    disabled={bulkUpdate || detailState?.tableLoading}
                  >
                    <span className={`icon-new-down-arrow-icon ${detailState.expandAllColumns ? 'icon-inverse' : ''}`} />
                  </button>
                </th>
                {renderSpecialColumnHeader('poRef', false, -1)}
                {renderSpecialColumnHeader('productName', false, -1)}
                {renderPinnedColumns?.map((col, ind) => renderColumn(col, ind, true))}
                {!filteredPinnedColumns.includes('supplier') && detailState?.selectedColumnShow?.includes('supplier') && (
                  <th className="w-224px">
                    <span className="truncate-text" title={t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SUPPLIER')}>
                      {t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SUPPLIER')}
                    </span>
                  </th>
                )}
                {!filteredPinnedColumns?.includes('factory') && detailState?.selectedColumnShow?.includes('factory') && (
                  <th className="w-224px">
                    <span className="truncate-text" title={t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.FACTORY')}>
                      {t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.FACTORY')}
                    </span>
                  </th>
                )}
                {!filteredPinnedColumns?.includes('colorway') && detailState?.selectedColumnShow?.includes('colorway') && (
                  <th className="w-224px">
                    <span className="truncate-text" title={t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.COLOURWAY')}>
                      {t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.COLOURWAY')}
                    </span>
                  </th>
                )}
                {individualDetail?.basedOnPO === true &&
                  !filteredPinnedColumns?.includes('skuNumber') &&
                  detailState?.selectedColumnShow?.includes('skuNumber') &&
                  renderSpecialColumnHeader('skuNumber', false, -1)}
                {individualDetail?.basedOnPO === true &&
                  !filteredPinnedColumns?.includes('transport') &&
                  detailState?.selectedColumnShow?.includes('transport') && (
                    <th className="w-170px">
                      <span className="truncate-text" title={t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.TRANSPORT')}>
                        {t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.TRANSPORT')}
                      </span>
                    </th>
                  )}
                {individualDetail?.basedOnPO === true && !filteredPinnedColumns?.includes('size') && detailState?.selectedColumnShow?.includes('size') && (
                  <th className="w-170px">
                    <span className="truncate-text" title={t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SIZE')}>
                      {t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SIZE')}
                    </span>
                  </th>
                )}
                {individualDetail?.basedOnPO === true &&
                  !filteredPinnedColumns?.includes('location') &&
                  detailState?.selectedColumnShow?.includes('location') &&
                  renderSpecialColumnHeader('location', false, -1)}
                {individualDetail?.basedOnPO === true &&
                  !filteredPinnedColumns?.includes('quantity') &&
                  detailState?.selectedColumnShow?.includes('quantity') &&
                  renderSpecialColumnHeader('quantity', false, -1)}
                {individualDetail?.basedOnPO === true &&
                  !filteredPinnedColumns?.includes('approvedByDate') &&
                  detailState?.selectedColumnShow?.includes('approvedByDate') &&
                  renderSpecialColumnHeader('approvedByDate', false, -1)}
                {individualDetail?.basedOnPO === true &&
                  !filteredPinnedColumns?.includes('exFactoryDate') &&
                  detailState?.selectedColumnShow?.includes('exFactoryDate') &&
                  renderSpecialColumnHeader('exFactoryDate', false, -1)}
                {individualDetail?.basedOnPO === true &&
                  !filteredPinnedColumns?.includes('shipmentDate') &&
                  detailState?.selectedColumnShow?.includes('shipmentDate') &&
                  renderSpecialColumnHeader('shipmentDate', false, -1)}
                {detailState?.columnsTab
                  ?.filter((col) => !selectedPinnedColumns?.includes(col._id) && (!showRequiredOnly || col.isRequired))
                  .map((col, ind) => renderColumn(col, ind, false))}
              </tr>
            </thead>

            {isEmpty(detailState?.tableProductDetail) && !detailState?.loading && !detailState?.isMainLoading && !detailState?.tableLoading ? (
              <tbody>
                <tr>
                  <td
                    colSpan={
                      7 +
                      (detailState?.columnsTab?.length || 0) +
                      (detailState?.selectedColumnShow?.includes('colorway') ? 1 : 0) +
                      (detailState?.selectedColumnShow?.includes('supplier') ? 1 : 0) +
                      (detailState?.selectedColumnShow?.includes('factory') ? 1 : 0) +
                      (individualDetail?.basedOnPO === true && detailState?.selectedColumnShow?.includes('skuNumber') ? 1 : 0) +
                      (individualDetail?.basedOnPO === true && detailState?.selectedColumnShow?.includes('transport') ? 1 : 0) +
                      (individualDetail?.basedOnPO === true && detailState?.selectedColumnShow?.includes('size') ? 1 : 0) +
                      (individualDetail?.basedOnPO === true && detailState?.selectedColumnShow?.includes('location') ? 1 : 0) +
                      (individualDetail?.basedOnPO === true && detailState?.selectedColumnShow?.includes('quantity') ? 1 : 0) +
                      (individualDetail?.basedOnPO === true && detailState?.selectedColumnShow?.includes('approvedByDate') ? 1 : 0) +
                      (individualDetail?.basedOnPO === true && detailState?.selectedColumnShow?.includes('exFactoryDate') ? 1 : 0) +
                      (individualDetail?.basedOnPO === true && detailState?.selectedColumnShow?.includes('shipmentDate') ? 1 : 0)
                    }
                  >
                    <p className="mb-0">{t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.PRODUCT_CRITICAL_PATH')}</p>
                  </td>
                </tr>
              </tbody>
            ) : (
              !isEmpty(detailState?.tableProductDetail) &&
              detailState?.tableProductDetail?.map((devEle, devIndex) => (
                <tbody key={`parent_${devEle?.rowKey}_${devIndex}`}>
                  <tr>
                    {!isProductArchived && (
                      <td>
                        <div className="common-checkbox margin-auto">
                          <input
                            type="checkbox"
                            onChange={(e) => onSelectCheckBox(e, devEle?.rowKey)}
                            name={`checks_${devEle?.rowKey}`}
                            id={`checks_${devEle?.rowKey}`}
                            checked={detailState?.selectedColumns?.includes(devEle?.rowKey)}
                          />
                          <label htmlFor={`checks_${devEle?.rowKey}`} className="chklabel" />
                        </div>
                      </td>
                    )}
                    <td>{devEle?.expandData?.length > 0 && expandButtons(devEle, devIndex)}</td>
                    <td className="w-170px">{shortenandHoverString(devEle?.poNumber, 20, 'auto')}</td>
                    <td className={isPinnedColum ? 'w-170px ' : 'w-170px first-fix-col '}>
                      <div className="d-flex align-items-center">
                        <span>{shortenandHoverString(devEle?.poProductNumber || devEle?.devOrderDetails?.number || null, 20, 'auto')}</span>
                        {devEle?.devOrderDetails?._id && (
                          <Link
                            target="_blank"
                            to={`/development-order/${devEle?.devOrderDetails?._id}/design-details/list`}
                            className="ml-auto text-decoration-none"
                          >
                            <span className="icon-redirect f-20" />
                          </Link>
                        )}
                      </div>
                    </td>
                    {renderPinnedColumns?.map((columnElement, columnIndex) => renderCell(columnElement, columnIndex, devEle, true))}
                    {!filteredPinnedColumns?.includes('supplier') && detailState?.selectedColumnShow?.includes('supplier') && (
                      <td>{shortenandHoverString(devEle?.supplierNames, 20, 'auto')}</td>
                    )}
                    {!filteredPinnedColumns?.includes('factory') && detailState?.selectedColumnShow?.includes('factory') && (
                      <td>{shortenandHoverString(devEle?.supplierFactories, 20, 'auto')}</td>
                    )}
                    {!filteredPinnedColumns?.includes('colorway') && detailState?.selectedColumnShow?.includes('colorway') && (
                      <td>{shortenandHoverString(devEle?.colourwayName, 20, 'auto')}</td>
                    )}
                    {individualDetail?.basedOnPO === true &&
                      !filteredPinnedColumns?.includes('skuNumber') &&
                      detailState?.selectedColumnShow?.includes('skuNumber') && (
                        <td className="w-170px">
                          <span>{shortenandHoverString(devEle?.skuNumber || null, 20, 'auto')}</span>
                        </td>
                      )}
                    {individualDetail?.basedOnPO === true &&
                      !filteredPinnedColumns?.includes('transport') &&
                      detailState?.selectedColumnShow?.includes('transport') && (
                        <td className="w-170px">
                          <span>
                            {devEle?.transport
                              ? shortenandHoverString(transportArray.find((ele) => ele.name === devEle.transport)?.label || devEle.transport, 20, 'auto')
                              : null}
                          </span>
                        </td>
                      )}
                    {individualDetail?.basedOnPO === true && !filteredPinnedColumns?.includes('size') && detailState?.selectedColumnShow?.includes('size') && (
                      <td className="w-170px">
                        <span>{shortenandHoverString(devEle?.sizeName || devEle?.sizeId || null, 20, 'auto')}</span>
                      </td>
                    )}
                    {individualDetail?.basedOnPO === true &&
                      !filteredPinnedColumns?.includes('location') &&
                      detailState?.selectedColumnShow?.includes('location') && (
                        <td className="w-170px">
                          <span>{shortenandHoverString(devEle?.location || null, 20, 'auto')}</span>
                        </td>
                      )}
                    {individualDetail?.basedOnPO === true &&
                      !filteredPinnedColumns?.includes('quantity') &&
                      detailState?.selectedColumnShow?.includes('quantity') && (
                        <td className="w-170px">
                          <span>{shortenandHoverString(devEle?.quantity || null, 20, 'auto')}</span>
                        </td>
                      )}
                    {individualDetail?.basedOnPO === true &&
                      !filteredPinnedColumns?.includes('approvedByDate') &&
                      detailState?.selectedColumnShow?.includes('approvedByDate') && (
                        <td className="w-170px">
                          <span>{shortenandHoverString(devEle?.approvedByDate || null, 20, 'auto')}</span>
                        </td>
                      )}
                    {individualDetail?.basedOnPO === true &&
                      !filteredPinnedColumns?.includes('exFactoryDate') &&
                      detailState?.selectedColumnShow?.includes('exFactoryDate') && (
                        <td className="w-170px">
                          <span>{shortenandHoverString(devEle?.exFactoryDate || null, 20, 'auto')}</span>
                        </td>
                      )}
                    {individualDetail?.basedOnPO === true &&
                      !filteredPinnedColumns?.includes('shipmentDate') &&
                      detailState?.selectedColumnShow?.includes('shipmentDate') && (
                        <td className="w-170px">
                          <span>{shortenandHoverString(devEle?.shipmentDate || null, 20, 'auto')}</span>
                        </td>
                      )}
                    {detailState?.columnsTab
                      ?.filter((col) => !selectedPinnedColumns?.includes(col._id) && (!showRequiredOnly || col.isRequired))
                      .map((columnElement, columnIndex) => renderCell(columnElement, columnIndex, devEle, false))}
                  </tr>
                  {devEle?.isExpanded &&
                    devEle?.expandData?.map((expandEle, expandIndex) => {
                      let childRowKey = expandEle?.rowKey;
                      if (!childRowKey && expandEle) {
                        const foundRow = detailState?.tableColumData?.find(
                          (data) =>
                            data?.poId?.toString() === expandEle?.poId?.toString() &&
                            data?.poOrderDetailIndex === expandEle?.poOrderDetailIndex &&
                            data?.productId?.toString() === expandEle?.productId?.toString() &&
                            (!data?.supplierId || !expandEle?.supplierId || data?.supplierId?.toString() === expandEle?.supplierId?.toString()) &&
                            (!data?.factoryId || !expandEle?.factoryId || data?.factoryId?.toString() === expandEle?.factoryId?.toString()) &&
                            (!data?.colourId || !expandEle?.colourId || data?.colourId?.toString() === expandEle?.colourId?.toString()),
                        );
                        childRowKey = foundRow?.rowKey;
                      }
                      if (!childRowKey) {
                        const poIdStr = expandEle?.poId?.toString() || devEle?.poId?.toString() || 'null';
                        const productIdStr = expandEle?.productId?.toString() || devEle?.productId?.toString() || 'null';
                        const supplierIdStr = expandEle?.supplierId?.toString() || 'null';
                        const factoryIdStr = expandEle?.factoryId?.toString() || 'null';
                        const colourIdStr = expandEle?.colourId?.toString() || 'null';
                        const skuNumber = expandEle?.skuNumber || 'null';
                        const transport = expandEle?.transport || 'null';
                        const sizeIdStr = expandEle?.sizeId?.toString() || 'null';
                        const locationValue = expandEle?.location || 'null';
                        const packType = expandEle?.packType || 'null';
                        childRowKey = `${poIdStr}_${productIdStr}_${supplierIdStr}_${factoryIdStr}_${colourIdStr}_${skuNumber}_${transport}_${sizeIdStr}_${locationValue}_${packType}`;
                      }
                      return (
                        <tr className="expand-row" key={`expanded_${devEle?.rowKey}_${expandIndex}`}>
                          {!isProductArchived && (
                            <td>
                              <div className="common-checkbox margin-auto">
                                <input
                                  type="checkbox"
                                  onChange={(e) => onSelectCheckBox(e, childRowKey)}
                                  name={`checks_${childRowKey}`}
                                  id={`checks_${childRowKey}`}
                                  checked={detailState?.selectedColumns?.includes(childRowKey)}
                                />
                                <label htmlFor={`checks_${childRowKey}`} className="chklabel" />
                              </div>
                            </td>
                          )}
                          {isProductArchived && <td />}
                          <td />
                          <td className="w-170px">{shortenandHoverString(expandEle.poNumber, 20, 'auto')}</td>
                          <td className={isPinnedColum ? 'w-170px ' : 'w-170px first-fix-col '}>
                            <div className="d-flex align-items-center">
                              <span>{shortenandHoverString(expandEle?.poProductNumber || expandEle?.devOrderDetails?.number || null, 20, 'auto')}</span>
                              {expandEle?.devOrderDetails?._id && (
                                <Link
                                  target="_blank"
                                  to={`/development-order/${expandEle?.devOrderDetails?._id}/design-details/list`}
                                  className="ml-auto text-decoration-none"
                                >
                                  <span className="icon-redirect f-20" />
                                </Link>
                              )}
                            </div>
                          </td>
                          {renderPinnedColumns?.map((expCol, expColInd) => renderExpandedCell(expCol, expColInd, devEle, expandEle, expandIndex, true))}
                          {!filteredPinnedColumns?.includes('supplier') && detailState?.selectedColumnShow?.includes('supplier') && (
                            <td>{shortenandHoverString(expandEle?.supplierNames, 20, 'auto')}</td>
                          )}
                          {!filteredPinnedColumns?.includes('factory') && detailState?.selectedColumnShow?.includes('factory') && (
                            <td>{shortenandHoverString(expandEle?.supplierFactories, 20, 'auto')}</td>
                          )}
                          {!filteredPinnedColumns?.includes('colorway') && detailState?.selectedColumnShow?.includes('colorway') && (
                            <td>{shortenandHoverString(expandEle?.colourwayName || expandEle?.colourWayName, 20, 'auto')}</td>
                          )}
                          {individualDetail?.basedOnPO === true &&
                            !filteredPinnedColumns?.includes('skuNumber') &&
                            detailState?.selectedColumnShow?.includes('skuNumber') && (
                              <td className="w-170px">
                                <span>{shortenandHoverString(expandEle?.skuNumber || null, 20, 'auto')}</span>
                              </td>
                            )}
                          {individualDetail?.basedOnPO === true &&
                            !filteredPinnedColumns?.includes('transport') &&
                            detailState?.selectedColumnShow?.includes('transport') && (
                              <td className="w-170px">
                                <span>
                                  {expandEle?.transport
                                    ? shortenandHoverString(
                                        transportArray.find((ele) => ele.name === expandEle.transport)?.label || expandEle.transport,
                                        20,
                                        'auto',
                                      )
                                    : null}
                                </span>
                              </td>
                            )}
                          {individualDetail?.basedOnPO === true &&
                            !filteredPinnedColumns?.includes('size') &&
                            detailState?.selectedColumnShow?.includes('size') && (
                              <td className="w-170px">
                                <span>{shortenandHoverString(expandEle?.sizeName || expandEle?.sizeId || null, 20, 'auto')}</span>
                              </td>
                            )}
                          {individualDetail?.basedOnPO === true &&
                            !filteredPinnedColumns?.includes('location') &&
                            detailState?.selectedColumnShow?.includes('location') && (
                              <td className="w-170px">
                                <span>{shortenandHoverString(expandEle?.location || null, 20, 'auto')}</span>
                              </td>
                            )}
                          {individualDetail?.basedOnPO === true &&
                            !filteredPinnedColumns?.includes('quantity') &&
                            detailState?.selectedColumnShow?.includes('quantity') && (
                              <td className="w-170px">
                                <span>{shortenandHoverString(expandEle?.quantity || null, 20, 'auto')}</span>
                              </td>
                            )}
                          {individualDetail?.basedOnPO === true &&
                            !filteredPinnedColumns?.includes('approvedByDate') &&
                            detailState?.selectedColumnShow?.includes('approvedByDate') && (
                              <td className="w-170px">
                                <span>{shortenandHoverString(expandEle?.approvedByDate || null, 20, 'auto')}</span>
                              </td>
                            )}
                          {individualDetail?.basedOnPO === true &&
                            !filteredPinnedColumns?.includes('exFactoryDate') &&
                            detailState?.selectedColumnShow?.includes('exFactoryDate') && (
                              <td className="w-170px">
                                <span>{shortenandHoverString(expandEle?.exFactoryDate || null, 20, 'auto')}</span>
                              </td>
                            )}
                          {individualDetail?.basedOnPO === true &&
                            !filteredPinnedColumns?.includes('shipmentDate') &&
                            detailState?.selectedColumnShow?.includes('shipmentDate') && (
                              <td className="w-170px">
                                <span>{shortenandHoverString(expandEle?.shipmentDate || null, 20, 'auto')}</span>
                              </td>
                            )}
                          {detailState?.columnsTab
                            ?.filter((col) => !selectedPinnedColumns?.includes(col._id) && (!showRequiredOnly || col.isRequired))
                            .map((expCol, expColInd) => renderExpandedCell(expCol, expColInd, devEle, expandEle, expandIndex, false))}
                        </tr>
                      );
                    })}
                </tbody>
              ))
            )}
          </table>
        </InfiniteScroll>
      </div>
    );
  };

  return (
    <div>
      {detailState?.loading ? (
        <Container fluid className="py-4">
          <Loader />
        </Container>
      ) : (
        renderMaintable()
      )}
    </div>
  );
}

export default DetailTablePO;
